import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CreditCard, ArrowClockwise, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";

const STATUS = {
  created:  { cls: "bg-[#F9E4B7] text-[#7A5A11]", icon: ArrowClockwise },
  captured: { cls: "bg-[#D4E0D1] text-[#1A3317]", icon: CheckCircle },
  failed:   { cls: "bg-[#F4C7B9] text-[#7A2E18]", icon: XCircle },
  refunded: { cls: "bg-[#E5E2D9] text-[#5C5A55]", icon: ArrowClockwise },
  orphan:   { cls: "bg-[#F4C7B9] text-[#7A2E18]", icon: Warning },
};

export default function RazorpayPayments() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({ mode: "test", key_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const r = await api.get(`/payments${q}`);
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => {
    api.get("/payments/razorpay/config").then(r => setCfg(r.data)).catch(() => {});
  }, []);

  const totals = items.reduce((acc, p) => {
    if (p.status === "captured") { acc.captured += Number(p.amount || 0); acc.cap_n++; }
    else if (p.status === "failed") acc.failed_n++;
    else if (p.status === "created") acc.pending_n++;
    return acc;
  }, { captured: 0, cap_n: 0, failed_n: 0, pending_n: 0 });

  return (
    <AppShell title="Razorpay Payments">
      <div className="space-y-5" data-testid="rzp-payments-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Finance · Online</div>
            <h1 className="font-heading text-2xl text-[#1A1A1A]">Razorpay Payments</h1>
            <div className="text-xs text-[#5C5A55] mt-1">All transactions captured by the Razorpay gateway, by tenant.</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-[0.2em] ${cfg.mode === "live" ? "bg-[#FBE7DD] text-[#7A2E18]" : "bg-[#E5E2D9] text-[#5C5A55]"}`}
                  data-testid="rzp-mode-badge">
              {cfg.mode || "—"} mode
            </span>
            <span className="text-[10px] font-mono text-[#5C5A55]">{cfg.key_id}</span>
            <button onClick={load} data-testid="rzp-refresh"
                    className="h-9 px-3 inline-flex items-center gap-1 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]">
              <ArrowClockwise size={12} weight="bold" /> Refresh
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Captured" value={`₹${totals.captured.toFixed(2)}`} sub={`${totals.cap_n} txn`} tone="green" testid="kpi-captured" />
          <Kpi label="Pending" value={totals.pending_n} sub="Order created" tone="amber" testid="kpi-pending" />
          <Kpi label="Failed" value={totals.failed_n} sub="Last 500" tone="red" testid="kpi-failed" />
          <Kpi label="Total rows" value={items.length} sub="In window" tone="grey" testid="kpi-total" />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="rzp-filters">
          {["", "captured", "created", "failed", "refunded", "orphan"].map(s => (
            <button key={s || "all"} data-testid={`rzp-filter-${s || "all"}`}
                    onClick={() => setStatusFilter(s)}
                    className={`h-8 px-3 rounded-md text-[11px] uppercase tracking-[0.18em] border ${statusFilter === s ? "bg-[#2D5A27] border-[#2D5A27] text-white" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
              {s || "All"}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="rzp-payments-table">
            <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              <tr>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Invoice</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Order ID</th>
                <th className="px-4 py-3 text-left">Payment ID</th>
                <th className="px-4 py-3 text-left">Method</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-6 py-10 text-center text-[#8A8780]">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-16 text-center text-[#8A8780]">
                  <CreditCard size={28} className="inline-block mb-2 opacity-40" />
                  <div className="text-xs">No Razorpay transactions yet.</div>
                </td></tr>
              )}
              {items.map((p, i) => {
                const st = STATUS[p.status] || STATUS.created;
                const Icon = st.icon;
                return (
                  <tr key={p.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} data-testid={`rzp-row-${p.id}`}>
                    <td className="px-4 py-3 text-xs text-[#5C5A55] whitespace-nowrap">{(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {p.sr_nos?.length > 1 ? (
                        <div data-testid={`rzp-srs-${p.id}`}>
                          <div>{p.sr_nos.join(", ")}</div>
                          <div className="text-[9px] uppercase tracking-[0.16em] text-[#2D5A27] font-sans">Prepaid · {p.sr_nos.length} SRs · one payment</div>
                        </div>
                      ) : (p.invoice_no || "—")}
                    </td>
                    <td className="px-4 py-3 text-xs">{p.customer_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-[10px] text-[#5C5A55] break-all">{p.razorpay_order_id}</td>
                    <td className="px-4 py-3 font-mono text-[10px] text-[#5C5A55] break-all">{p.razorpay_payment_id || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-medium uppercase">{p.method || "—"}</div>
                      {p.card_last4 && <div className="text-[10px] text-[#8A8780]">**** {p.card_last4}</div>}
                      {p.vpa && <div className="text-[10px] text-[#8A8780]">{p.vpa}</div>}
                      {p.bank && <div className="text-[10px] text-[#8A8780]">{p.bank}</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">₹{Number(p.amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1 ${st.cls}`}>
                        <Icon size={10} weight="bold" />{p.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] text-[#8A8780]">
          Webhook URL: <span className="font-mono">{(process.env.REACT_APP_BACKEND_URL || "")}/api/payments/razorpay/webhook</span> ·
          Add this in your Razorpay dashboard and paste the secret into <code>RAZORPAY_WEBHOOK_SECRET</code> in <code>backend/.env</code>.
        </div>
      </div>
    </AppShell>
  );
}

function Kpi({ label, value, sub, tone = "grey", testid }) {
  const toneCls = {
    green: "bg-[#D4E0D1] text-[#1A3317]",
    amber: "bg-[#F9E4B7] text-[#7A5A11]",
    red:   "bg-[#F4C7B9] text-[#7A2E18]",
    grey:  "bg-[#E5E2D9] text-[#5C5A55]",
  }[tone];
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-4" data-testid={testid}>
      <div className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] rounded ${toneCls}`}>{label}</div>
      <div className="font-heading text-2xl mt-2 text-[#1A1A1A]">{value}</div>
      <div className="text-[10px] text-[#8A8780] mt-0.5">{sub}</div>
    </div>
  );
}
